import { Component, inject, signal } from '@angular/core';
import { DialogErrorBar } from '../../core/dialogs/dialog-error-bar/dialog-error-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { form, FormField, maxLength, minLength, required } from '@angular/forms/signals';
import { FormFieldWrapper } from '../../core/form/form-field-wrapper/form-field-wrapper';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../../core/services/notification-service/notification-service';
import { defer } from 'rxjs';
import { dialogSubmission } from '../../core/dialogs/services/dialog-mutation-service/dialog-submission';
import { UpdateProfileRequest } from '../models/auth.models';

const NAME_MIN = 2;
const NAME_MAX = 30;

@Component({
  selector: 'app-edit-profile-dialog',
  imports: [
    DialogErrorBar,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIcon,
    FormField,
    FormFieldWrapper,
  ],
  templateUrl: './edit-profile-dialog.html',
})
export class EditProfileDialog {
  readonly dialogRef = inject(MatDialogRef<EditProfileDialog>);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);

  readonly submission = dialogSubmission({ clearErrorOn: () => this.model() });

  private readonly user = this.authService.currentUser();

  model = signal<UpdateProfileRequest>({
    firstName: this.user?.firstName ?? '',
    lastName: this.user?.lastName ?? '',
  });

  profileForm = form(this.model, (schemaPath) => {
    required(schemaPath.firstName, { message: 'Jméno je prázdné' });
    minLength(schemaPath.firstName, NAME_MIN, {
      message: `Jméno musí mít alespoň ${NAME_MIN} znaky`,
    });
    maxLength(schemaPath.firstName, NAME_MAX, {
      message: `Jméno může mít maximálně ${NAME_MAX} znaků`,
    });
    required(schemaPath.lastName, { message: 'Příjmení je prázdné' });
    minLength(schemaPath.lastName, NAME_MIN, {
      message: `Příjmení musí mít alespoň ${NAME_MIN} znaky`,
    });
    maxLength(schemaPath.lastName, NAME_MAX, {
      message: `Příjmení může mít maximálně ${NAME_MAX} znaků`,
    });
  });

  private markAllAsTouched(): void {
    this.profileForm.firstName().markAsTouched();
    this.profileForm.lastName().markAsTouched();
  }

  async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();
    this.markAllAsTouched();

    if (this.profileForm().invalid()) {
      this.notificationService.showValidationError('Formulář není správně vyplněn');
      return;
    }

    const outcome = await this.submission.run(
      defer(() => this.authService.updateProfile(this.model())),
      'EditProfileDialog.updateProfile',
    );
    if (outcome.outcome === 'success') {
      this.notificationService.showSuccess('Jméno bylo úspěšně změněno');
    }
  }
}
